import React, { useMemo, type RefObject, type ReactNode, useRef } from 'react';
import groupBy from 'lodash/groupBy';
import noop from 'lodash/noop';
import type { OptionType, OptionProps } from '@atlaskit/select';

import type { GroupBase, OptionsOrGroups } from '@atlaskit/react-select';
import type { AtlaskitSelectRefType } from '@atlaskit/select/types';
import { Box, xcss, Flex } from '@atlaskit/primitives';
import { GIC_VIEW_SOURCE } from '@atlassian/jira-issue-type-config-common/src/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { FormatMessage } from '@atlassian/jira-shared-types/src/general.tsx';
import { EditIssueTypeUFOExperience } from '@atlassian/jira-issue-create-analytics/src/services/experiences/index.tsx';
import type {
	IssueType,
	RequestType,
	RequestTypePractices,
	IssueOrRequestType,
} from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';
import { GroupComponentWithStickyFooter } from '@atlassian/jira-select-with-footer-options/src/GroupComponentWithStickyFooter.tsx';
import {
	SERVICE_REQUEST,
	CHANGE_MANAGEMENT,
	PROBLEM_MANAGEMENT,
	INCIDENT_MANAGEMENT,
	POST_INCIDENT_REVIEW,
	DEVELOPER_ESCALATION,
	ISSUE_TYPE,
	REQUEST_TYPE,
	UNASSIGNED_REQUEST_TYPE,
} from '@atlassian/jira-issue-create-common-types/src/common/constants/graphql-field-types.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import {
	useAppendStickyFooterOptions,
	type FooterOption,
} from '@atlassian/jira-select-with-footer-options/src/utils.tsx';
import { OptionWithButtonStyle } from '@atlassian/jira-select-with-footer-options/src/OptionWithButtonStyle.tsx';
import { OptionWithAutoKeyboardScroll } from '@atlassian/jira-select-with-footer-options/src/OptionWithAutoKeyboardScroll.tsx';
import { EditIssueTypeModalTrigger } from '@atlassian/jira-edit-issue-type-modal-trigger/src/EditIssueTypeModalTrigger.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';

import {
	OPTION_TYPE_CREATE_ISSUE_TYPE,
	OPTION_TYPE_EDIT_ISSUE_TYPE,
	OPTION_TYPE_MANAGE_ISSUE_TYPE,
	REFOCUS_DELAY_MS,
} from './constants.tsx';

import type {
	RequestTypeOption,
	RequestAndIssueTypeOptions,
	NoneOption,
} from './request-type-select/types.tsx';
import type {
	CustomOptionQueryParams,
	CustomOptionsAndComponentsForInlineIssueTypesProps,
} from './types.tsx';
import messages from './messages.tsx';
import { CreateWorkTypeButton } from './create-work-type-button/CreateWorkTypeButton.tsx';

export const getFieldLabel = (hasRequestTypes: boolean, formatMessage: FormatMessage): string =>
	hasRequestTypes
		? formatMessage(messages.typeFieldLabel)
		: formatMessage(
				expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
					? messages.issueTypeFieldLabelIssueTermRefresh
					: messages.issueTypeFieldLabel,
			);

export const getRequestTypeGroupLabel = (
	practice: RequestTypePractices,
	formatMessage: FormatMessage,
): string => {
	if (practice === CHANGE_MANAGEMENT) {
		return formatMessage(messages.changeRequestGroupLabel);
	}
	if (practice === INCIDENT_MANAGEMENT) {
		return formatMessage(messages.incidentRequestGroupLabel);
	}
	if (practice === PROBLEM_MANAGEMENT) {
		return formatMessage(messages.problemRequestGroupLabel);
	}
	if (practice === SERVICE_REQUEST) {
		return formatMessage(messages.serviceRequestGroupLabel);
	}
	if (practice === POST_INCIDENT_REVIEW) {
		return formatMessage(messages.postIncidentReviewRequestGroupLabel);
	}
	if (practice === DEVELOPER_ESCALATION) {
		return formatMessage(messages.developerEscalationRequestGroupLabel);
	}
	if (practice === UNASSIGNED_REQUEST_TYPE) {
		return formatMessage(messages.unassignedRequestTypeGroupLabel);
	}

	return formatMessage(messages.requestTypeGroupLabel);
};

const reorderRequestTypeGroups = (
	requestTypeGroupKeys: RequestTypePractices[],
): RequestTypePractices[] => {
	// This is the order we have them for queues, request types and feature page
	const allPractices: RequestTypePractices[] = [
		SERVICE_REQUEST,
		INCIDENT_MANAGEMENT,
		PROBLEM_MANAGEMENT,
		CHANGE_MANAGEMENT,
		POST_INCIDENT_REVIEW,
		DEVELOPER_ESCALATION,
		UNASSIGNED_REQUEST_TYPE,
		REQUEST_TYPE,
	];
	const groupKeySet = new Set(requestTypeGroupKeys);
	return allPractices.filter((practice) => groupKeySet.has(practice));
};

const createNoneOption = (formatMessage: FormatMessage): NoneOption => ({
	label: formatMessage(messages.noRequestTypeLabel),
	value: 'none',
	type: 'NONE',
});

export const getRequestAndIssueTypeOptionsGroup = (
	issueTypes: IssueType[],
	requestTypes: RequestType[],
	formatMessage: FormatMessage,
): RequestAndIssueTypeOptions => {
	// @ts-expect-error(PARTIAL_RECORD) TS2322 | TS2322 - Type 'Dictionary<RequestType[]>' is not assignable to type 'Record<RequestTypePractices, RequestType[]>'. | Type 'Dictionary<RequestType[]>' is not assignable to type 'Record<RequestTypePractices, RequestType[]>'.
	const groupedRequestTypes: Record<RequestTypePractices, RequestType[]> = groupBy(
		requestTypes,
		(o) => o.practice,
	);

	// @ts-expect-error - TS2345 - Argument of type 'string[]' is not assignable to parameter of type 'RequestTypePractices[]'.
	const groupKeys = reorderRequestTypeGroups(Object.keys(groupedRequestTypes));
	const requestTypesOptions: RequestTypeOption[] = groupKeys.map((practice) => ({
		label: getRequestTypeGroupLabel(practice, formatMessage),
		options: groupedRequestTypes[practice],
	}));

	return {
		issueTypeOptions: issueTypes,
		requestTypeOptions: [createNoneOption(formatMessage), ...requestTypesOptions],
	};
};

export const getIssueTypeAndRequestType = ({
	requestOrIssueType,
	allIssueTypes,
	formatMessage,
}: {
	requestOrIssueType: IssueOrRequestType;
	allIssueTypes: IssueType[];
	formatMessage: FormatMessage;
}): {
	issueType: IssueType | null;
	requestType: RequestType | NoneOption | null;
} => {
	const noneOption = createNoneOption(formatMessage);

	let issueType: IssueType | null = null;
	let requestType: RequestType | NoneOption | null = null;

	if (requestOrIssueType !== null) {
		switch (requestOrIssueType.type) {
			case ISSUE_TYPE:
				issueType = requestOrIssueType;
				requestType = noneOption;
				break;
			case REQUEST_TYPE:
				issueType =
					allIssueTypes.find(({ value }) => value === requestOrIssueType.issueTypeId) || null;
				requestType = requestOrIssueType;
				break;
			default: // default case forced by flow
		}
	}

	return { issueType, requestType };
};

export const getFirstMatchingRequestType = ({
	issueType,
	allRequestTypes,
}: {
	issueType: IssueType;
	allRequestTypes: RequestType[];
}): RequestType | undefined =>
	allRequestTypes.find(({ issueTypeId }) => issueTypeId === issueType.value);

export const useOptionsWithFooterActions = (
	targetOptions: OptionType[],
): OptionsOrGroups<OptionType, GroupBase<OptionType>> => {
	const { formatMessage } = useIntl();
	// eslint-disable-next-line no-nested-ternary
	const createIssueOrWorkTypeMessage = fg('jira-issue-terminology-refresh-m3')
		? fg('jira_rename_inline_create_work_type_button')
			? messages.addWorkTypeButtonText
			: messages.createWorkTypeButtonTextIssueTermRefresh
		: fg('jira_rename_inline_create_work_type_button')
			? messages.addIssueTypeButtonText
			: messages.createIssueTypeButtonText;

	const editIssueOrWorkTypeMessage = fg('jira-issue-terminology-refresh-m3')
		? messages.editWorkTypeButtonTextIssueTermRefresh
		: messages.editIssueTypeButtonText;

	const manageIssueOrWorkTypesMessage = fg('jira-issue-terminology-refresh-m3')
		? messages.manageWorkTypesButtonTextIssueTermRefresh
		: messages.manageIssueTypesButtonText;

	const footerOptions: FooterOption[] = useMemo(() => {
		return [
			{
				label: formatMessage(createIssueOrWorkTypeMessage),
				type: OPTION_TYPE_CREATE_ISSUE_TYPE,
				value: OPTION_TYPE_CREATE_ISSUE_TYPE,
			},
			{
				label: formatMessage(editIssueOrWorkTypeMessage),
				type: OPTION_TYPE_EDIT_ISSUE_TYPE,
				value: OPTION_TYPE_EDIT_ISSUE_TYPE,
			},
			{
				label: formatMessage(manageIssueOrWorkTypesMessage),
				type: OPTION_TYPE_MANAGE_ISSUE_TYPE,
				value: OPTION_TYPE_MANAGE_ISSUE_TYPE,
			},
		];
	}, [
		formatMessage,
		createIssueOrWorkTypeMessage,
		editIssueOrWorkTypeMessage,
		manageIssueOrWorkTypesMessage,
	]);

	return useAppendStickyFooterOptions(targetOptions, footerOptions);
};

export const useCustomOptionForWorkTypeInlineConfig = ({
	onPreloadCreateIssueTypeModal = noop,
	options,
	...queryParams
}: CustomOptionQueryParams) => {
	return useMemo(() => {
		const CustomOptionForWorkTypeInlineConfig = ({
			children,
			...props
		}: OptionProps<OptionType>) => {
			const { createAnalyticsEvent } = useAnalyticsEvents();
			if (
				[OPTION_TYPE_CREATE_ISSUE_TYPE, OPTION_TYPE_MANAGE_ISSUE_TYPE].includes(props.data.type)
			) {
				return (
					<OptionWithButtonStyle
						testId={`issue-create.ui.modal.create-form.type-picker.option-with-button-${props.data.type}`}
						onHover={onPreloadCreateIssueTypeModal}
						{...props}
						innerProps={{
							...props.innerProps,
							role: 'button',
						}}
					>
						{children}
					</OptionWithButtonStyle>
				);
			}

			if (OPTION_TYPE_EDIT_ISSUE_TYPE === props.data.type) {
				const analyticsEvent = createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'button',
				});

				const {
					projectId,
					projectKey,
					issueTypeId,
					onEditModalClose,
					onRefetchIssueTypesWhenIssueTypeUpdated,
					editIssueTypeButtonRef,
				} = queryParams;
				return (
					<EditIssueTypeModalTrigger
						onIssueTypeUpdated={onRefetchIssueTypesWhenIssueTypeUpdated}
						ufoExperience={EditIssueTypeUFOExperience}
						onOpen={noop}
						onClose={onEditModalClose}
						projectId={projectId}
						projectKey={projectKey}
						calledFrom={GIC_VIEW_SOURCE}
						issueTypeId={issueTypeId}
						ref={editIssueTypeButtonRef}
						relevantIssueTypeIds={options.map((option) => option.value.toString())}
						onClick={() => fireUIAnalytics(analyticsEvent, 'createIssueTypeButton')}
					>
						<OptionWithButtonStyle
							testId={`issue-create.ui.modal.create-form.type-picker.option-with-button-${props.data.type}`}
							{...props}
						>
							{children}
						</OptionWithButtonStyle>
					</EditIssueTypeModalTrigger>
				);
			}

			return (
				<OptionWithAutoKeyboardScroll {...props}>
					<Flex xcss={optionContainerStyles}>{children}</Flex>
				</OptionWithAutoKeyboardScroll>
			);
		};
		return CustomOptionForWorkTypeInlineConfig;
	}, [onPreloadCreateIssueTypeModal, queryParams, options]);
};

export const useCustomOptionsAndComponentsForInlineIssueTypes = ({
	options,
	projectId,
	projectKey,
	issueTypeId,
	canConfigureIssueTypes,
	onPreloadCreateIssueTypeModal = noop,
	onLoadCreateIssueTypeModal = noop,
	onIssueTypeCreatedOrUpdated = noop,
	onEditModalClose,
	editIssueTypeButtonRef,
}: CustomOptionsAndComponentsForInlineIssueTypesProps) => {
	const noOptionWrapperRef = useRef<HTMLElement>(null);
	const CustomOptionWithInlineConfig = useCustomOptionForWorkTypeInlineConfig({
		projectId: Number(projectId),
		ufoExperience: EditIssueTypeUFOExperience,
		onRefetchIssueTypesWhenIssueTypeUpdated: onIssueTypeCreatedOrUpdated,
		calledFrom: GIC_VIEW_SOURCE,
		onEditModalClose,
		projectKey,
		issueTypeId,
		onPreloadCreateIssueTypeModal,
		editIssueTypeButtonRef,
		options,
	});

	const optionsWithActions = useOptionsWithFooterActions(options ?? []);

	if (!canConfigureIssueTypes) {
		return {
			options,
			components: undefined,
		};
	}

	const NoOptionsMessage = () => {
		const { formatMessage } = useIntl();
		return (
			<Box ref={noOptionWrapperRef} paddingInline="space.150" paddingBlock="space.050">
				{formatMessage(
					fg('jira-issue-terminology-refresh-m3')
						? messages.noSearchResultsIssueTermRefresh
						: messages.noSearchResultsOldTerm,
					{
						button: (chunks: ReactNode) => (
							<CreateWorkTypeButton
								onClick={onLoadCreateIssueTypeModal}
								onMouseEnter={onPreloadCreateIssueTypeModal}
							>
								{chunks}
							</CreateWorkTypeButton>
						),
					},
				)}
			</Box>
		);
	};

	return {
		options: optionsWithActions,
		components: {
			Group: GroupComponentWithStickyFooter,
			Option: CustomOptionWithInlineConfig,
			NoOptionsMessage,
		},
		noOptionWrapperRef,
	};
};

export const useRefocusSelectField = (selectRef: RefObject<AtlaskitSelectRefType>) => {
	return () => {
		// When Create issue type or Edit issue type modal is closed, the modal will automatically return focus to the issue type field
		// The returned focus is returning fine and works well for keyboard users, however, @atlaskit/select caused some weird bug which
		// made issue type field not clickable for mouse users until it gets a refocus after a while, if we refocus immediately it also won't work
		// We tried to refocus with requestIdleCallback and requestAnimationFrame to delay the focus point based on CPU Idle and frame Idle
		// but none of that works so we need to use setTimeout temporarily before ADS team fix this issue
		setTimeout(() => {
			// Refocusing to make issue type field clickable after focus is returned from closing modal
			selectRef.current?.blur();
			selectRef.current?.focus();
		}, REFOCUS_DELAY_MS);
	};
};

const optionContainerStyles = xcss({
	wordBreak: 'break-word',
});
